import type { AxiosRequestConfig } from "axios";

import { filenameFromContentDisposition } from "@/lib/download";

import apiClient from "./client";
import type { components } from "./generated/backend";
import { api } from "./typed";

/**
 * Policy pack acknowledgement (consultant-portal-backend#864).
 *
 * Published policies are listed with the caller's own status; a gated
 * consultant (`has_pending_policies` on the user) must acknowledge every
 * pending or outdated one before using the portal. PDFs are served behind
 * auth, so they come through the authenticated client as a Blob (same
 * reasoning as `cv.ts`), and the draft upload is multipart.
 */
export type MyPolicy = components["schemas"]["MyPolicyResponse"];
export type MyPolicies = components["schemas"]["MyPoliciesResponse"];
export type PolicyAckStatus = MyPolicy["status"];
export type PolicyDocument = components["schemas"]["PolicyDocumentAdminResponse"];
export type PolicyMatrix = components["schemas"]["PolicyMatrixResponse"];
export type PolicyMatrixRow = components["schemas"]["PolicyMatrixRow"];
export type PolicyRequires = PolicyDocument["requires"];

/** Pending or outdated: the policy still needs the consultant's acknowledgement. */
export const isOpenStatus = (status: PolicyAckStatus): boolean =>
  status === "pending" || status === "outdated";

export interface PolicyDraftInput {
  key: string;
  title: string;
  version: string;
  requires: PolicyRequires;
  effectiveDate?: string;
  file: File;
}

const MULTIPART = { headers: { "Content-Type": "multipart/form-data" } };

const BLOB_CONFIG: AxiosRequestConfig & { suppressErrorToast: boolean } = {
  responseType: "blob",
  suppressErrorToast: true,
};

export interface PolicyFile {
  blob: Blob;
  filename: string;
}

const fetchPdf = async (url: string, fallback: string): Promise<PolicyFile> => {
  const response = await apiClient.get<Blob>(url, BLOB_CONFIG);
  return {
    blob: response.data,
    filename: filenameFromContentDisposition(response.headers["content-disposition"]) ?? fallback,
  };
};

export const policiesAPI = {
  /** GET /policies: published policies with the caller's status. */
  listMine: (): Promise<MyPolicies> => api.get("/policies"),

  /**
   * The same list for the background gate re-check. Silent: a failed
   * re-check must not toast on every page (and never gates anyone).
   */
  checkGate: (): Promise<MyPolicies> =>
    api.get("/policies", { config: { suppressErrorToast: true } }),

  /** GET /policies/{id}/file: the PDF of a published policy. */
  getFile: (policyId: number): Promise<PolicyFile> =>
    fetchPdf(`/policies/${policyId}/file`, `policy-${policyId}.pdf`),

  /** POST /policies/{id}/acknowledge: accept, or mark a "read" policy as read. */
  acknowledge: (policyId: number) =>
    api.post("/policies/{policy_id}/acknowledge", { path: { policy_id: policyId } }),
};

export const adminPoliciesAPI = {
  /** GET /admin/policies: every version (draft, published, superseded). */
  list: () => api.get("/admin/policies"),

  /** POST /admin/policies: upload a PDF as a new draft version. */
  uploadDraft: async (input: PolicyDraftInput): Promise<PolicyDocument> => {
    const form = new FormData();
    form.append("key", input.key);
    form.append("title", input.title);
    form.append("version", input.version);
    form.append("requires", input.requires);
    if (input.effectiveDate) {
      form.append("effective_date", input.effectiveDate);
    }
    form.append("file", input.file);
    const response = await apiClient.post<PolicyDocument>("/admin/policies", form, MULTIPART);
    return response.data;
  },

  /** POST /admin/policies/{id}/publish: supersedes the current version of the key. */
  publish: (policyId: number) =>
    api.post("/admin/policies/{policy_id}/publish", { path: { policy_id: policyId } }),

  /** DELETE /admin/policies/{id}: drafts only. */
  deleteDraft: (policyId: number) =>
    api.delete("/admin/policies/{policy_id}", { path: { policy_id: policyId } }),

  /** GET /admin/policies/{id}/file: any version, including drafts. */
  getFile: (policyId: number): Promise<PolicyFile> =>
    fetchPdf(`/admin/policies/${policyId}/file`, `policy-${policyId}.pdf`),

  /** GET /admin/policies/overview: consultant × policy matrix. */
  overview: (): Promise<PolicyMatrix> => api.get("/admin/policies/overview"),

  /** POST /admin/policies/reminders: in-app reminder to consultants with open policies. */
  remind: (userIds?: number[]) =>
    api.post("/admin/policies/reminders", { body: { user_ids: userIds ?? null } }),
};
